import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'

import StepReviewerPicker from './StepReviewerPicker'
import { WorkflowFlowMember, WorkflowStepDraft } from './types'

interface StepDetailPanelProps {
  step: WorkflowStepDraft
  index: number
  total: number
  members: WorkflowFlowMember[]
  duplicateReviewerIds: Set<string>
  onPatch: (patch: Partial<WorkflowStepDraft>) => void
  onToggleReviewer: (memberId: string) => void
  onMove: (direction: 'left' | 'right') => void
  onDelete: () => void
  /** false = template mode: no reviewers is a valid skeleton, shown as a hint
   *  instead of an error (Freelo #31947103). */
  requireReviewers?: boolean
}

/**
 * Everything about the selected step that does not fit on its node. Validation
 * is surfaced here exactly as the old form-list editor did — as hints, never as
 * a block: `onPatch` always fires and the parent decides submittability.
 */
const StepDetailPanel: React.FC<StepDetailPanelProps> = ({
  step,
  index,
  total,
  members,
  duplicateReviewerIds,
  onPatch,
  onToggleReviewer,
  onMove,
  onDelete,
  requireReviewers = true,
}) => {
  const { t } = useTranslation()

  const hasDuplicate = step.reviewerIds.some((id) => duplicateReviewerIds.has(id))

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Badge variant="secondary" className="shrink-0">
            {index + 1}
          </Badge>
          <span className="truncate text-sm font-semibold">{t('Submittals_wf_flow_step_detail')}</span>
        </div>
        <div className="flex shrink-0 items-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onMove('left')}
            disabled={index === 0}
            title={t('Submittals_wf_flow_move_left')}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onMove('right')}
            disabled={index === total - 1}
            title={t('Submittals_wf_flow_move_right')}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`wf-step-name-${step.key}`}>{t('Submittals_wf_step_name_label')}</Label>
        <Input
          id={`wf-step-name-${step.key}`}
          value={step.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder={t('Submittals_wf_step_name_placeholder')}
          className="h-8 text-sm"
        />
        {!step.name.trim() && (
          <p className="text-xs text-destructive">{t('Submittals_wf_step_name_required')}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`wf-step-duration-${step.key}`}>{t('Submittals_wf_step_duration_label')}</Label>
        <div className="flex items-center gap-2">
          <Input
            id={`wf-step-duration-${step.key}`}
            type="number"
            min={1}
            value={step.durationDays ?? ''}
            onChange={(e) => onPatch({ durationDays: e.target.value === '' ? null : Number(e.target.value) })}
            className="h-8 w-20 text-sm"
          />
          <span className="text-sm text-muted-foreground">{t('Submittals_wf_step_days')}</span>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label>{t('Submittals_wf_flow_reviewers')}</Label>
          <Badge variant="secondary">{step.reviewerIds.length}</Badge>
        </div>
        <StepReviewerPicker step={step} members={members} onToggleReviewer={onToggleReviewer} />
        {step.reviewerIds.length === 0 && (
          requireReviewers ? (
            <p className="text-xs text-destructive">{t('Submittals_wf_step_no_reviewers')}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t('Submittals_wf_step_reviewers_at_apply', 'Reviewers are assigned when the template is used.')}
            </p>
          )
        )}
        {hasDuplicate && (
          <p className="text-xs text-destructive">{t('Submittals_wf_duplicate_reviewer')}</p>
        )}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full text-destructive hover:text-destructive"
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
        {t('Submittals_wf_flow_delete_step')}
      </Button>
    </div>
  )
}

export default StepDetailPanel
